/* eslint-disable import/no-extraneous-dependencies */

import Swiper from 'swiper';
import {
    Keyboard,
    Navigation,
    Pagination,
    Scrollbar,
    A11y,
    Thumbs,
} from 'swiper/modules';

import addProduct from './components/addProduct';
import { modalTrigger } from './modals';
import { infoTooltipModal } from './checkout/homeClubHelpers';
import { debounce, scrollLock, appendToUrl } from './util';
import { equalizeElements } from './checkout/membershipHelpers';
import {
    KEYBOARD_KEYS,
    handleEscapeKey,
    trapModalFocus,
    removeModalFocus,
    saveFocus, // to be used in the future
    restoreFocus, // to be used in the future

} from './a11yHelper';
import GtmParametersBuilder from './gtm/gtmParametersBuilder';

const Details = (() => {
    let qlSwiper;
    let glDayListSwiper;
    let glDateSwiper;
    let equipmentsSwiper;
    let clubsSwiper;
    let membershipSwiper;

    // Initializes Swiper for Hero Club Details Page
    const initSwipers = () => {
        const $swipers = $('.js-club-swiper');

        // Separate helper for a11y labels
        const handlePaginationAccessibility = (swiperInstance, id) => {
            const $paginationWrapper = $(`.js-club-swiper-pagination-${id}`);
            const goToSlideLabel = $paginationWrapper.data('goto-slide-label') || (window.resources && window.resources['slider.goto.slide.label']);
            const currentSlideLabel = $paginationWrapper.data('current-slide-label') || (window.resources && window.resources['slider.current.slide.label']);
            const ofLabel = $paginationWrapper.data('of-label') || (window.resources && window.resources['slider.of.label']);

            const $paginationBullets = $paginationWrapper.find('.swiper-pagination-bullet');
            const totalSlides = swiperInstance.slides.length;

            $paginationBullets.each(function handlePaginationBullet(index) {
                const isActive = index === swiperInstance.realIndex;
                const label = isActive
                    ? `${currentSlideLabel} ${index + 1} ${ofLabel} ${totalSlides}`
                    : `${goToSlideLabel} ${index + 1} ${ofLabel} ${totalSlides}`;

                $(this)
                    .attr('tabindex', '0')
                    .attr('role', 'button')
                    .attr('aria-label', label);
            });

            $paginationBullets.off('keydown').on('keydown', function handlePaginationKeydown(e) {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    $(this).trigger('click');
                }
            });
        };

        const swiperIntersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && entry.intersectionRatio > 0) {
                    const swiper = entry.target;
                    const $swiper = $(swiper);

                    if ($swiper.length < 1) {
                        return;
                    }

                    const id = $swiper.attr('id');

                    const options = {
                        modules: [Navigation, Pagination, Keyboard],
                        loop: true,
                        spaceBetween: 0,
                        slidesPerView: 1,
                        breakpointsInverse: true,
                        watchOverflow: true,
                        pagination: {
                            el: `.js-club-swiper-pagination-${id}`,
                            type: 'bullets',
                            clickable: true,
                        },
                        keyboard: {
                            enabled: false, // we handle manually
                            onlyInViewport: false,
                        },
                        navigation: {
                            nextEl: `.js-club-swiper-next-${id}`,
                            prevEl: `.js-club-swiper-prev-${id}`,
                        },
                        on: {
                            init() {
                                // Ensure slides are unfocusable
                                this.slides.forEach((slide) => {
                                    slide.setAttribute('tabindex', '-1');
                                });
                            },
                            slideChange() {
                                // Ensure slides remain unfocusable
                                this.slides.forEach((slide) => {
                                    slide.setAttribute('tabindex', '-1');
                                });

                                // Wait for the slide change to finish before updating the live region
                                setTimeout(() => {
                                    const activeSlide = this.slides[this.activeIndex];
                                    const img = activeSlide.querySelector('img');

                                    if (img && img.alt) {
                                        const liveRegion = document.getElementById('hero-slide-live-region');

                                        if (liveRegion) {
                                            // Temporarily clear the content to force screen reader to notice change
                                            liveRegion.textContent = ''; // Temporarily clear
                                            setTimeout(() => {
                                                liveRegion.textContent = img.alt; // Set the alt text again
                                            }, 50); // 50ms delay before resetting text content
                                        }
                                    }
                                }, 300); // Wait for the slide transition to complete
                            },
                        },
                    };

                    const swiperInstance = new Swiper(`#${id}`, options);

                    swiperInstance.on('init', () => {
                        swiperInstance.update(); // Ensure DOM sync
                        handlePaginationAccessibility(swiperInstance, id);
                    });

                    swiperInstance.emit('init');

                    // Keyboard support for nav buttons
                    const handleKeyDown = (event) => {
                        const { key } = event;
                        const $target = $(event.target);

                        if (key === KEYBOARD_KEYS.ENTER || key === KEYBOARD_KEYS.SPACE) {
                            event.preventDefault();

                            // Store reference to the button
                            const targetElement = event.target;

                            if ($target.hasClass(`js-club-swiper-next-${id}`)) {
                                swiperInstance.slideNext();
                            } else if ($target.hasClass(`js-club-swiper-prev-${id}`)) {
                                swiperInstance.slidePrev();
                            }

                            // Restore focus after a short delay
                            setTimeout(() => {
                                targetElement.focus();
                            }, 300); // small timeout ensures Swiper updates DOM first
                        }
                    };

                    $(`.js-club-swiper-next-${id}, .js-club-swiper-prev-${id}`)
                        .attr('tabindex', '0')
                        .on('keydown', handleKeyDown);

                    swiperIntersectionObserver.unobserve(swiper);
                }
            });
        });

        $swipers.each((_, swiper) => {
            swiperIntersectionObserver.observe(swiper);
        });
    };

    // Initializes Quick Links Swiper for mobile only
    const quickLinkSwiper = () => {
        const wrapperClass = '.js-club-detail-quick-links';
        const swiperClass = '.js-quick-link-swiper';
        const slideNumber = $(swiperClass).find('.swiper-slide').length;
        const goTo = $('.js-go-to');
        const nextArrow = $(`${wrapperClass} .js-arrow-next`);
        const prevArrow = $(`${wrapperClass} .js-arrow-prev`);

        if (!$(swiperClass).length) {
            return;
        }

        // Updates the `goTo` class based on swiper position. Works for both clicks and swipes.
        const updateGoToClass = (swiper) => {
            if (swiper.isEnd || swiper.isBeginning) {
                goTo.removeClass('active'); // Remove class when at first or last slide
            } else {
                goTo.addClass('active'); // Add class otherwise
            }
        };

        const options = {
            modules: [Navigation, A11y],
            speed: 500,
            loop: false,
            spaceBetween: 16,
            slidesPerView: 'auto',
            breakpointsInverse: true,
            watchOverflow: true,
            touchRatio: 1,
            navigation: {
                nextEl: `${wrapperClass} .js-arrow-next`,
                prevEl: `${wrapperClass} .js-arrow-prev`,
            },
            a11y: {
                prevSlideMessage: window.resources['slider.prev.slide.label'],
                nextSlideMessage: window.resources['slider.next.slide.label'],
                firstSlideMessage: window.resources['slider.first.slide.label'],
                lastSlideMessage: window.resources['slider.last.slide.label'],
            },
            breakpoints: {
                768: {
                    spaceBetween: 32,
                },
            },
            on: {
                slideChange(swiper) {
                    updateGoToClass(swiper);
                },
            },
        };

        if (typeof qlSwiper === 'undefined' && slideNumber > 1) {
            qlSwiper = new Swiper(swiperClass, options);
        }

        // Handles arrow click events. Ensures class updates correctly
        const handleArrowClick = () => {
            if (window.innerWidth < 768 && qlSwiper) {
                updateGoToClass(qlSwiper);
            }
        };

        // Attach event listeners
        nextArrow.on('click', handleArrowClick);
        prevArrow.on('click', handleArrowClick);
    };

    // Enables keyboard navigation to function after clicking on the quick links.
    const scrollToLinks = () => {
        const $quickLinkContainer = $('.js-quick-link-swiper');

        if ($quickLinkContainer.length) {
            $quickLinkContainer.find('a[href^="#"]').on('click', function onClickHandler() {
                const targetId = $(this).attr('href');
                const $targetElement = $(targetId);

                if ($targetElement.length) {
                    // Delay focus slightly to allow smooth scrolling
                    setTimeout(() => {
                        $targetElement.attr('tabindex', '-1').focus();
                    }, 100);
                }
            });
        }
    };

    /**
     * Initializes Group Lessons Dates Swiper (thumbnails)
     */
    const groupLessonsDateThumbsSwiper = () => {
        const swiperClass = '.js-group-lessons-date-swiper';
        const $swiper = $(swiperClass);
        const slideNumber = $swiper.find('.swiper-slide').length;

        if ($swiper.length < 1) {
            return;
        }

        const options = {
            modules: [Navigation, A11y, Thumbs, Keyboard],
            speed: 500,
            loop: false,
            spaceBetween: 4,
            slidesPerView: 'auto',
            breakpointsInverse: true,
            watchOverflow: true,
            touchRatio: 1,
            freeMode: true,
            watchSlidesProgress: true,
            a11y: {
                prevSlideMessage: window.resources['slider.prev.slide.label'],
                nextSlideMessage: window.resources['slider.next.slide.label'],
                firstSlideMessage: window.resources['slider.first.slide.label'],
                lastSlideMessage: window.resources['slider.last.slide.label'],
            },
            keyboard: {
                enabled: true,
                onlyInViewport: false,
            },
        };

        if (typeof glDateSwiper === 'undefined' && slideNumber > 1) {
            glDateSwiper = new Swiper(swiperClass, options);

            // Handle Enter key for navigation buttons (Days)
            $(document).on('keydown', (event) => {
                if (event.key === KEYBOARD_KEYS.ENTER) {
                    const $focusedElement = $(document.activeElement);

                    if ($focusedElement.hasClass('arrow-next')) {
                        glDateSwiper.slideNext(); // Move to the next slide
                    } else if ($focusedElement.hasClass('arrow-prev')) {
                        glDateSwiper.slidePrev(); // Move to the previous slide
                    } else if ($focusedElement.hasClass('swiper-slide-active')) {
                        $focusedElement[0].click();
                    }
                }
            });
        }
    };

    /**
     * Initializes Group Lessons Full Day List Swiper
     */
    const groupLessonsDayListSwiper = () => {
        const wrapperClass = '.js-group-lessons-modal-content';
        const swiperClass = '.js-group-lessons-day-list-swiper';
        const $swiper = $(swiperClass);
        const slideNumber = $swiper.find('.swiper-slide').length;

        if ($swiper.length < 1) {
            return;
        }

        const options = {
            modules: [Navigation, A11y, Thumbs],
            speed: 500,
            loop: false,
            slidesPerView: 1,
            breakpointsInverse: true,
            watchOverflow: true,
            touchRatio: 1,
            spaceBetween: 16,
            navigation: {
                nextEl: `${wrapperClass} .arrow-next`,
                prevEl: `${wrapperClass} .arrow-prev`,
            },
            thumbs: {
                swiper: glDateSwiper,
                slideThumbActiveClass: 'active',
            },
            a11y: {
                prevSlideMessage: `${window.resources['slider.prev.slide.label']}`,
                nextSlideMessage: `${window.resources['slider.next.slide.label']}`,
                firstSlideMessage: `${window.resources['slider.first.slide.label']}`,
                lastSlideMessage: `${window.resources['slider.last.slide.label']}`,
            },
            keyboard: {
                enabled: false,
                onlyInViewport: false,
            },
        };

        if (typeof glDayListSwiper === 'undefined' && slideNumber > 1) {
            glDayListSwiper = new Swiper(swiperClass, options);
        }
    };

    /**
     * Initializes Group Lessons Detail schedule Days
     */
    const groupLessonsDayListEnter = () => {
        const $dateSwiper = $('.js-group-lessons-date-swiper');
        const $dayListSwiper = $('.js-group-lessons-day-list-swiper');
        let $lastFocusedDateSlide = null;

        if (!$dateSwiper.length || !$dayListSwiper.length) return;

        const $modalContainer = $('.group-lessons-modal-content-inner'); // Your focus trap container

        trapModalFocus($modalContainer);

        $dateSwiper.on('keydown', '.swiper-slide', function handleDateSwiperKeydown(event) {
            if (event.key === KEYBOARD_KEYS.ENTER || event.key === KEYBOARD_KEYS.SPACE) {
                event.preventDefault();

                $lastFocusedDateSlide = $(this); // Store the last focused date slide
                const targetLabel = $lastFocusedDateSlide.attr('aria-label');
                if (!targetLabel) return;

                // Find the corresponding slide in js-group-lessons-day-list-swiper
                const $targetSlide = $dayListSwiper.find('.swiper-slide').filter(function filterSlides() {
                    return $(this).attr('aria-label') === targetLabel;
                }).first();

                if ($targetSlide.length) {
                    const $firstFocusableInSlide = $targetSlide.find('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])').first();
                    if ($firstFocusableInSlide.length) {
                        $firstFocusableInSlide.trigger('focus'); // Move focus, but don't re-trap
                    }
                }
            }
        });

        $dayListSwiper.on('keydown', (event) => {
            const $activeSlide = $dayListSwiper.find('.swiper-slide-active');
            const $focusableElements = $activeSlide.find('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            const currentIndex = $focusableElements.index($(document.activeElement));

            if (event.key === KEYBOARD_KEYS.ESC && $lastFocusedDateSlide) {
                event.preventDefault();
                event.stopPropagation(); // Prevent triggering the global escape handler
                $lastFocusedDateSlide.trigger('focus'); // Return focus to the previously focused date slide
            }

            // Prevent moving focus out of the active slide in dayListSwiper using Tab key
            if (event.key === KEYBOARD_KEYS.TAB) {
                if ((event.shiftKey && currentIndex === 0) || (!event.shiftKey && currentIndex === $focusableElements.length - 1)) {
                    event.preventDefault();
                }
            }
        });
    };

    /**
     * Initializes Equipments Swiper
     */
    function initEquipmentSwiper() {
        const swiperClass = '.js-equipment-swiper';
        const $swiperElement = $(swiperClass);
        const slideNumber = $swiperElement.find('.swiper-slide').length;

        if (!$swiperElement.length || slideNumber <= 1) {
            return;
        }

        const options = {
            modules: [Navigation, Pagination, Scrollbar, A11y, Keyboard],
            spaceBetween: 16,
            slidesPerView: 1.2,
            breakpointsInverse: true,
            watchOverflow: true,
            scrollbar: {
                el: '.equipment-swiper-scrollbar',
                draggable: true,
            },
            navigation: {
                nextEl: '.equipment-swiper-arrow-next',
                prevEl: '.equipment-swiper-arrow-prev',
            },
            keyboard: {
                enabled: true,
                onlyInViewport: false,
            },
            a11y: {
                prevSlideMessage: window.resources['slider.prev.slide.label'],
                nextSlideMessage: window.resources['slider.next.slide.label'],
                firstSlideMessage: window.resources['slider.first.slide.label'],
                lastSlideMessage: window.resources['slider.last.slide.label'],
            },
            breakpoints: {
                550: {
                    slidesPerView: 2.2,
                },
                901: {
                    slidesPerView: 2.6,
                },
            },
        };

        if (typeof equipmentsSwiper === 'undefined') {
            equipmentsSwiper = new Swiper(swiperClass, options);

            // Focus the swiper container when navigation arrows are clicked.
            $('.equipment-swiper-arrow-next, .equipment-swiper-arrow-prev').on('click', () => {
                $swiperElement.trigger('focus');
            });

            // Keydown event for navigation arrows
            $('.equipment-swiper-arrow-next, .equipment-swiper-arrow-prev').on('keydown', function handleEquipmentSwiperKeydown(event) {
                if (event.key === KEYBOARD_KEYS.ENTER) {
                    event.preventDefault();
                    if ($(this).hasClass('equipment-swiper-arrow-next')) {
                        equipmentsSwiper.slideNext();
                    } else {
                        equipmentsSwiper.slidePrev();
                    }
                }
            });
        }
    }

    /**
     * Initializes Clubs Nearby Swiper
     */
    function clubsNearbySlider() {
        const swiperClass = '.js-clubs-nearby-slider';
        const slideNumber = $(swiperClass).find('.swiper-slide').length;

        if (!$(swiperClass).length > 0) {
            return;
        }

        const options = {
            modules: [Navigation, Pagination, Scrollbar, A11y],
            spaceBetween: 24,
            slidesPerView: 1,
            breakpointsInverse: true,
            watchOverflow: true,
            scrollbar: {
                el: '.js-clubs-nearby-slider-scrollbar',
                draggable: true,
            },
            navigation: {
                nextEl: '.js-clubs-nearby-slider-next',
                prevEl: '.js-clubs-nearby-slider-prev',
            },
            a11y: {
                prevSlideMessage: `${window.resources['slider.prev.slide.label']}`,
                nextSlideMessage: `${window.resources['slider.next.slide.label']}`,
                firstSlideMessage: `${window.resources['slider.first.slide.label']}`,
                lastSlideMessage: `${window.resources['slider.last.slide.label']}`,
            },
            breakpoints: {
                768: {
                    slidesPerView: 1.67,
                },
                901: {
                    spaceBetween: 32,
                    slidesPerView: 3.5,
                },
            },
        };

        if (typeof clubsSwiper === 'undefined' && slideNumber > 1) {
            clubsSwiper = new Swiper(swiperClass, options);
        }
    }

    /**
     * Initializes membership swiper
     * We currently never have more then 3 tiles on the CDP, the template and css take a 4th tile into account for a PD component (so we keep a swiper on desktop)
     */
    const handleSwiperMembership = () => {
        const swiperClass = '.js-membership-slider';
        const slideNumber = $(swiperClass).find('.swiper-slide').length;

        if (!$(swiperClass).length > 0) {
            return;
        }

        membershipSwiper = undefined;
        const minWidthTablet = 768;
        const minWidthDesktop = 1025;

        const options = {
            modules: [Navigation, Pagination, A11y],
            spaceBetween: 12,
            slidesPerView: 1,
            breakpointsInverse: true,
            centeredSlides: true,
            watchOverflow: true,
            pagination: {
                el: '.js-membership-slider-pagination',
                type: 'bullets',
                clickable: true,
            },
            navigation: {
                nextEl: '.js-membership-slider-next',
                prevEl: '.js-membership-slider-prev',
            },
            a11y: {
                prevSlideMessage: `${window.resources['slider.prev.slide.label']}`,
                nextSlideMessage: `${window.resources['slider.next.slide.label']}`,
                firstSlideMessage: `${window.resources['slider.first.slide.label']}`,
                lastSlideMessage: `${window.resources['slider.last.slide.label']}`,
            },
            breakpoints: {
                [minWidthTablet]: {
                    spaceBetween: 72,
                    slidesPerView: 2,
                    centeredSlides: true,
                },
                [minWidthDesktop]: {
                    slidesPerView: 'auto',
                    spaceBetween: 8,
                    centeredSlides: false,
                },
            },
        };

        if (typeof membershipSwiper === 'undefined' && slideNumber > 1) {
            membershipSwiper = new Swiper(swiperClass, options);
            $(`${swiperClass} .swiper-slide`).each((index, element) => {
                if ($(element).find('.js-membership-block').hasClass('has-top-label')) {
                    membershipSwiper.slideTo(index);
                    return false;
                }
                return true;
            });
        }
    };

    // Equalize heights
    const equalizeClubsNearby = () => {
        equalizeElements('.js-club-nearby-tile h3.title');
        equalizeElements('.js-club-nearby-tile .addressdetails');
    };

    // Equalize heights for prices (so they all space up evenly when extra pricing is injected)
    const equalizeMembershipPrices = () => {
        equalizeElements('.membership-tile__header');
        equalizeElements('.membership-tile__footer');
    };

    /**
     * Repositioning and animating page scroll to cater for sticky header overlap.
     * @param {string} hash - The hash of the target element to scroll to.
     */
    const scrollToHash = (hash) => {
        const $target = $(hash);
        const header = document.querySelector('.main-page-header');
        const headerHeight = header ? header.offsetHeight : 0;

        if ($target.length) {
            $('html, body').stop().animate({
                scrollTop: $target.offset().top - headerHeight,
            }, 500, () => {
                if (history.pushState) {
                    history.pushState(null, null, hash);
                } else {
                    window.location.hash = hash;
                }
            });
        }
    };

    /**
     * Initializes event listeners for the Club Details page
     */
    const initEventListeners = () => {
        const $body = $('body');
        const $heroPopup = $('.js-hero-popup');
        const $heroPopupCloseButton = $('.js-modal-close');
        const $heroPopupOpenButton = $('.js-view-more');

        infoTooltipModal('from-side');

        $body.on('click', '.js-join', (e) => {
            e.preventDefault();

            const $target = $(e.target);

            if ($target.hasClass('disabled')) {
                return;
            }

            const id = $target.attr('data-pid');
            const membership = $target.attr('data-membership-id');
            const duration = $target.attr('data-duration');

            addProduct(id, membership, null, duration);

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({
                event: 'precheckout',
                step: '2',
                component_name: 'select club',
            });
        });

        // ****** Modal heroPopup ********//

        /**
         * Pauses and rewinds the video if applicable
         * @param {string} act - The action of the detail view, either 'pause' or 'rewind'
         */
        const resetVideo = (act = 'pause') => {
            const $clubVideo = $('#club-video-3d');

            if ($clubVideo.length > 0) {
                const video = $clubVideo.get(0);
                video.pause();

                if (act === 'rewind') {
                    video.currentTime = 0;
                }
            }
        };

        /**
         * Reloads iframe if applicable
         */
        const reloadIframe = () => {
            const $iFrame = $('#club-frame-360');

            if ($iFrame.length > 0) {
                const src = $iFrame.attr('src');
                $iFrame.attr('src', src);
            }
        };

        /**
         * Handles content inside the hero pop-up
         * @param {Event} e - Event object
         */
        const handleHeroPopupContent = (e) => {
            const $target = $(e.target);
            const $tabs = $('.js-hero-popup-tab');
            const $contentItems = $('.hero-popup-content-item');
            const id = $target.attr('id');

            const focusableSelector = 'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])';

            // Update tabs
            $tabs.each((i, tab) => {
                const $tab = $(tab);
                const isSelected = $tab.attr('id') === id;

                $tab.toggleClass('hero-popup-tab--highlighted', isSelected);
                $tab.attr('aria-selected', isSelected);
                resetVideo(); // if needed
            });

            // Update content panels
            $contentItems.each((i, contentItem) => {
                const $contentItem = $(contentItem);
                const isActive = $contentItem.hasClass(`js-hero-popup-${id}-content`);

                if (isActive) {
                    $contentItem
                        .addClass('hero-popup-content-item--active')
                        .removeAttr('inert')
                        .attr('aria-hidden', 'false');

                    // Set focus to first visible focusable item inside panel
                    setTimeout(() => {
                        // Find the first visible iframe or first visible focusable element inside the panel
                        const $focusableIframe = $contentItem.find('iframe').filter(':visible').first();
                        const $otherFocusable = $contentItem.find(focusableSelector).filter(':visible').first();

                        // Pick iframe if present, else first other focusable
                        const $toFocus = $focusableIframe.length ? $focusableIframe : $otherFocusable;

                        if ($toFocus && $toFocus.length) {
                            $toFocus.attr('tabindex', '0').trigger('focus');
                        } else {
                            // If no focusable elements found, focus the panel container itself
                            $contentItem.attr('tabindex', '0').trigger('focus');
                        }
                    }, 0);
                } else {
                    $contentItem
                        .removeClass('hero-popup-content-item--active')
                        .attr('inert', '')
                        .attr('aria-hidden', 'true')
                        .attr('tabindex', '-1');
                }
            });
        };

        /**
         * Handles the hero popup content when the page loads
         */
        $body.on('click', '.js-view-more', () => {
            modalTrigger($heroPopup, 'open', 'up-down', 'hm-overlay');
            $heroPopup.removeAttr('inert');

            // Set focus on the close button when modal opens
            setTimeout(() => {
                $heroPopupCloseButton.trigger('focus');
                trapModalFocus($('#dialog-hero')); // Ensure focus trap is applied
            }, 200);
        });

        /**
         * Close Modal HeroPopup on Click
         */
        const closeHeroModal = () => {
            modalTrigger($heroPopup, 'close', 'up-down', 'hm-overlay');
            resetVideo('rewind');
            reloadIframe();
            $heroPopup.attr('inert', '');

            setTimeout(() => {
                // Return focus to the button that opened the modal
                $heroPopupOpenButton.trigger('focus');
            }, 100);
        };

        // Close on click
        $body.on('click', '.js-modal-close, .js-hero-modal-backdrop', closeHeroModal);

        // Close on Escape key
        $(document).on('keydown', (event) => {
            handleEscapeKey(event, () => {
                if ($heroPopup.hasClass('show')) {
                    closeHeroModal();
                }
            });
        });

        /**
         * Modal: HeroPopup Inside Link tabs Navigation
         */
        $body.on('click', '.js-hero-popup-tab', handleHeroPopupContent);
        // ****** End Modal heroPopup ********//

        $body.on('click', '.js-acc-trigger', (e) => {
            const $target = $(e.target);
            $target.toggleClass('active');
        });

        $body.on('click', '.js-quick-link-slide a', (e) => {
            e.preventDefault();

            const target = e.target.hash;
            scrollToHash(target);
        });

        // Global Variable
        let lastFocusedElement = null; // Store the last focused element

        // View Full list Modal
        $body.on('click', '.js-view-full-list', (e) => {
            lastFocusedElement = e.currentTarget; // Store the clicked button
            const $target = $(e.target);
            const $accParent = $target.closest('.acc-content');
            const $panelModal = $accParent.find('.panel-modal');
            const $equipmentModal = $accParent.find('.js-equipment-modal');
            const $workoutModal = $accParent.find('.js-video-workouts-modal');
            let $modal;

            if ($panelModal.length === 1) {
                $modal = $panelModal;
                $body.addClass('pm-overlay');
                $modal.addClass('show');
                $modal.removeAttr('inert'); // Remove inert when opening
                scrollLock('on');
            } else if ($equipmentModal.length === 1) {
                $modal = $equipmentModal;
                $modal.removeAttr('inert'); // Remove inert when opening
                modalTrigger($modal, 'open', 'up-down', 'em-overlay');
            } else if ($workoutModal.length === 1) {
                $modal = $workoutModal;
                $modal.removeAttr('inert'); // Remove inert when opening
                modalTrigger($modal, 'open', 'up-down', 'em-overlay');
            }

            if ($modal && $modal.length) {
                setTimeout(() => {
                    // Find the correct close button for the opened modal
                    const $closeButton = $modal.find('.js-panel-popup-close, .js-equipment-modal-close');
                    if ($closeButton.length) {
                        $closeButton.trigger('focus'); // Use jQuery trigger to avoid TypeScript warning
                    }
                    trapModalFocus($modal); // Trap focus inside the modal
                }, 100);
            }
        });

        // Closing of various modals and their overlays (also clicking on these overlays)
        $body.on('click', '.js-panel-popup-close, .js-tt-ms-overlay, .js-equipment-modal-close', (e) => {
            const $target = $(e.target);
            const $accParent = $target.closest('.acc-content');
            const $panelModal = $accParent.find('.panel-modal');
            const $equipmentModal = $accParent.find('.js-equipment-modal');
            const $workoutModal = $accParent.find('.js-video-workouts-modal');
            const $groupLessonParent = $target.closest('.js-group-lessons-section');
            const $groupModal = $groupLessonParent.find('.group-lessons-modal');
            const $classDetailsWrapper = $('.js-class-details-wrapper');
            const $groupLessonsMainModalContent = $('.js-group-lessons-modal-content');
            let $modal;

            if ($panelModal.length === 1 || $target.hasClass('js-tt-ms-overlay')) {
                $('.panel-modal, .js-equipment-modal').removeClass('show');
                $body.removeClass('pm-overlay em-overlay');
                scrollLock('off');
                removeModalFocus();
            } else if ($equipmentModal.length === 1) {
                $modal = $equipmentModal;
                modalTrigger($modal, 'close', 'up-down', 'em-overlay');
            } else if ($workoutModal.length === 1) {
                $modal = $workoutModal;
                modalTrigger($modal, 'close', 'up-down', 'em-overlay');
            }
            if ($groupModal.length === 1) {
                $modal = $groupModal;
                modalTrigger($modal, 'close', 'up-down', 'gl-overlay');
                $classDetailsWrapper.removeClass('active');
                $groupLessonsMainModalContent.removeClass('hide');
            }

            if (lastFocusedElement) {
                setTimeout(() => {
                    $(lastFocusedElement).trigger('focus');
                    $(lastFocusedElement).next('.js-equipment-modal, .js-video-workouts-modal, .js-group-classes-popup').attr('inert', '');
                }, 100);
            }
        });

        // ****** Start of Group lessons  ********//

        // Close Group Lessons Modal
        $body.on('click', '.js-group-lessons-modal-close', (e) => {
            const $target = $(e.target);
            const $groupLessonParent = $target.closest('.js-group-lessons-section');
            const $groupModal = $groupLessonParent.find('.group-lessons-modal');
            const $classDetailsWrapper = $('.js-class-details-wrapper');
            const $groupLessonsMainModalContent = $('.js-group-lessons-modal-content');

            if ($groupModal.length === 1) {
                modalTrigger($groupModal, 'close', 'up-down', 'gl-overlay');

                setTimeout(() => {
                    if (lastFocusedElement) {
                        lastFocusedElement.focus(); // Restore focus to the last clicked element
                    }
                }, 100);

                // Reset modal content state
                $classDetailsWrapper.removeClass('active').attr('inert', '');
                $groupLessonsMainModalContent.removeClass('hide').attr('inert', ''); // Remove inert attribute from main modal content
            }
        });

        // Close Button when inside the Group Lessons Details after clicking js-view-schedule
        $body.on('keydown', '.js-group-lessons-modal-close-inside', (e) => {
            const $target = $(e.target);
            const $groupLessonParent = $target.closest('.js-group-lessons-section');
            const $groupModal = $groupLessonParent.find('.group-lessons-modal');
            const $classDetailsWrapper = $('.js-class-details-wrapper');
            const $groupLessonsMainModalContent = $('.js-group-lessons-modal-content');

            if (e.key === KEYBOARD_KEYS.ENTER || e.key === KEYBOARD_KEYS.ESC) {
                e.preventDefault();
                $('.js-view-schedule').trigger('focus');
                if ($groupModal.length === 1) {
                    modalTrigger($groupModal, 'close', 'up-down', 'gl-overlay');
                    // Reset modal content state
                    $classDetailsWrapper.removeClass('active').attr('inert', '');
                    $groupLessonsMainModalContent.removeClass('hide').attr('inert', '');
                }
            }
        });

        let $lastFocusedTile = null; // Store last focused tile

        // Track the last focused group lesson tile
        $body.on('click', '.js-group-lessons-tile', function handleGroupLessonsTileClick() {
            if (!$(this).closest('.js-group-lessons-wrapper').length) {
                $lastFocusedTile = $(this);
            }
        });

        // Back Button on the Group Lessons Details
        $body.on('click keydown', '.js-class-details-modal-back', (event) => {
            if (event.type === 'click' || event.key === KEYBOARD_KEYS.ENTER || event.key === KEYBOARD_KEYS.ESC) {
                event.preventDefault();
                event.stopPropagation(); // Prevent triggering the global escape handler
                const $classDetailsWrapper = $('.js-class-details-wrapper');
                const $groupLessonsMainModalContent = $('.js-group-lessons-modal-content');
                const $closeGroupLessons = $('.js-group-lessons-modal-close');

                $classDetailsWrapper.removeClass('active');
                $groupLessonsMainModalContent.removeClass('hide');
                $groupLessonsMainModalContent.removeAttr('inert');

                setTimeout(() => {
                    if ($lastFocusedTile && $lastFocusedTile.length) {
                        $lastFocusedTile.focus(); // Restore focus to last selected tile
                    } else {
                        $closeGroupLessons.trigger('focus'); // Fallback to close button
                    }
                }, 100);
            }
        });

        // Open Group Lessons Full Schedule Group
        $body.on('click', '.js-view-schedule', (e) => {
            lastFocusedElement = $(e.target);

            const $groupLessonsMainModalContent = $('.js-group-lessons-modal-content');
            const $closeGroupLessons = $('.js-group-lessons-modal-close-second');
            const $groupModal = $('.js-group-lessons-modal');

            if ($groupModal.length) {
                modalTrigger($groupModal, 'open', 'up-down', 'gl-overlay');
            }

            setTimeout(() => {
                requestAnimationFrame(() => {
                    $groupLessonsMainModalContent.removeAttr('inert');
                    trapModalFocus($groupLessonsMainModalContent);

                    const isVisible = $closeGroupLessons.is(':visible');
                    const isInert = $closeGroupLessons.attr('inert') !== undefined;
                    const canFocus = $closeGroupLessons[0] && typeof $closeGroupLessons[0].focus === 'function';

                    if (isVisible && !isInert && canFocus) {
                        $closeGroupLessons[0].focus();
                    }
                });
            }, 350);
        });

        // Open Group Lessons Dialog Individual
        $body.on('click', '.js-group-lessons-tile', (e) => {
            const $target = $(e.target);
            const $tile = $target.closest('.js-group-lessons-tile');
            const $groupLessonParent = $tile.closest('.js-group-lessons-section');
            const $groupModal = $('.group-lessons-modal');
            const url = $groupLessonParent.attr('data-url');
            const storeID = $groupLessonParent.attr('data-store-id');
            const activity = $tile.attr('data-id') || '';
            const activityID = activity.includes('&') ? activity.replace('&', '-') : activity;
            const groupLessonsModalClose = $('.js-group-lessons-modal-close');
            const $groupLessonsModal = $target.closest('.js-group-lessons-modal');

            // Save the element that opened the modal
            lastFocusedElement = $tile;

            const urlParameters = { isGroupLessonsModalClick: $groupLessonsModal.length > 0 };
            const groupLessonsModalIsOpen = $('.js-group-lessons-modal').hasClass('show');
            if (storeID) urlParameters.club_id = storeID;
            if (activityID) urlParameters.activityID = activityID;

            const action = appendToUrl(url, urlParameters);

            $.ajax({
                url: action,
                type: 'GET',
                dataType: 'json',
                success: (data) => {
                    const $classDetailsWrapper = $('.js-class-details-wrapper');
                    const $groupLessonsMainModalContent = $('.js-group-lessons-modal-content');
                    const $groupLessonsDetailsBack = $('.js-class-details-modal-back');

                    $classDetailsWrapper.html(data.eventHtml); // Load the HTML content
                    $classDetailsWrapper.addClass('active').removeAttr('inert');
                    $groupLessonsMainModalContent.addClass('hide').attr('inert', '');

                    // Check if Group lessons Modal is already open
                    if (!groupLessonsModalIsOpen) {
                        modalTrigger($groupModal, 'open', 'up-down', 'gl-overlay');

                        setTimeout(() => {
                            groupLessonsModalClose.trigger('focus');

                            requestAnimationFrame(() => {
                                trapModalFocus($classDetailsWrapper); // Ensure keyboard focus stays inside
                            });
                        }, 350); // Slightly longer delay to ensure the modal is in the DOM
                    } else {
                        setTimeout(() => {
                            $groupLessonsDetailsBack.trigger('focus');
                            // If the modal is already open, just focus on the close button

                            requestAnimationFrame(() => {
                                trapModalFocus($classDetailsWrapper); // Ensure keyboard focus stays inside
                            });
                        }, 350); // Slightly longer delay to ensure the modal is in the DOM
                    }
                },
            });
        });

        // ****** End Group Lesson ********//

        $body.on('click', '.js-store-tile-lead-gen-club, .js-store-tile-presale-club', (e) => {
            e.preventDefault();
            const $target = $(e.target);
            const $tile = $target.closest('.js-club-nearby-tile');
            const storeId = $tile.attr('data-pid');
            const url = $tile.hasClass('js-store-tile-lead-gen-club') ? $tile.attr('data-lead-gen-url') : $tile.attr('data-pre-sale-url');

            window.location = `${url}?sid=${storeId}`;
        });

        // A11y: Escape key (tt modals have their own)
        $(document).on('keydown', (event) => {
            if (event.key === KEYBOARD_KEYS.ESC) {
                let $modal;

                if ($('.js-hero-popup').hasClass('show')) {
                    $modal = $('.js-hero-popup');
                    modalTrigger($modal, 'close', 'up-down', 'hm-overlay');
                    resetVideo('rewind');
                    reloadIframe();
                } else if ($('.js-group-lessons-modal').hasClass('show')) {
                    $modal = $('.js-group-lessons-modal');
                    modalTrigger($modal, 'close', 'up-down', 'gl-overlay');
                    $('.js-class-details-wrapper').removeClass('active');
                    $('.js-group-lessons-modal-content').removeClass('hide');
                } else if ($('.panel-modal').hasClass('show')) {
                    $modal = $('.panel-modal');
                    $body.removeClass('pm-overlay');
                    $modal.removeClass('show');
                } else if ($('.js-equipment-modal').hasClass('show')) {
                    $modal = $('.js-equipment-modal');
                    modalTrigger($modal, 'close', 'up-down', 'em-overlay');
                }

                // Ensure focus returns to the opener
                if ($modal && lastFocusedElement && $(lastFocusedElement).is(':visible')) {
                    setTimeout(() => {
                        $(lastFocusedElement).trigger('focus');
                        $(lastFocusedElement).next('.js-equipment-modal, .js-video-workouts-modal, .js-group-classes-popup').attr('inert', '');
                    }, 100);
                }

                if ($('body').hasClass('scroll-lock')) {
                    scrollLock('off');
                }
            }
        });

        $(window).on('load', () => {
            if (window.location.hash) {
                scrollToHash(window.location.hash);
            }
        });

        $(window).on('hashchange', () => {
            if (window.location.hash) {
                scrollToHash(window.location.hash);
            }
        });
    };

    const initGtmEvents = () => {
        window.dataLayer = window.dataLayer || [];

        // START send club_detail_view event
        const MAIN_CONTENT_ID = '#main-content';

        // get GTM data from main-content element on page
        const gtmData = $(MAIN_CONTENT_ID).data();
        const club = {
            club_name: gtmData.gtmStoreName,
            club_id: gtmData.gtmStoreId,
            club_visuals: gtmData.gtmStoreVisuals,
            club_services: gtmData.gtmStoreServices,
            club_facilities: gtmData.gtmStoreFacilities,
        };

        // send event
        const event = new GtmParametersBuilder()
            .withEvent('club_detail_view')
            .withParameter('club', club)
            .build();

        window.dataLayer.push(event);
        // END send club_detail_view event

        // START send go_to_checkout event
        const JOIN_BUTTON_IDENTIFIER = '.js-join';

        $(JOIN_BUTTON_IDENTIFIER).on('click', (e) => {
            const elementData = $(e.target).data();

            if (elementData.ga) {
                const eventBuilder = new GtmParametersBuilder()
                    .withEvent('go_to_checkout')
                    .withParameter('button_type', elementData.ga);

                if (elementData.gtmMembership) {
                    eventBuilder.withParameter('membership', elementData.gtmMembership);
                }

                const goToCheckoutEvent = eventBuilder.build();

                window.dataLayer.push(goToCheckoutEvent);
            }
        });
        // END send go_to_checkout event
    };

    /**
     * Read More : About Us and Find Section
     * Example: https://staging-cc.basic-fit.com/en-nl/clubs/basic-fit-almere-dwangmolenstraat-24-7-e5be8a04ffac421ca519e062b9f7338a.html
     */

    const initReadMoreToggle = (options) => {
        const $body = $('body');
        const {
            triggerSelector,
            text1Selector,
            text2Selector,
            readMoreTextKey,
            readLessTextKey,
        } = options;

        function handleReadMoreClick(event) {
            const $button = $(event.currentTarget);
            const isExpanded = $button.attr('aria-expanded') === 'true';

            const $text1 = $(text1Selector);
            const $text2 = $(text2Selector);

            if (isExpanded) {
                $button.attr('aria-expanded', 'false')
                    .text(window.resources[readMoreTextKey])
                    .removeClass('active');
                $text1.removeClass('show-foldout-text-2');
                $text2.attr('inert', '');
            } else {
                $button.attr('aria-expanded', 'true')
                    .text(window.resources[readLessTextKey])
                    .addClass('active');
                $text1.addClass('show-foldout-text-2');
                $text2.removeAttr('inert');
            }
        }

        $body.on('click', triggerSelector, handleReadMoreClick);
    };

    const initMembershipUspToggle = () => {
        $('.js-usp-toggle').on('click', function handleUspToggleClick() {
            const $button = $(this);
            const listId = $button.attr('aria-controls');
            const $list = $(`#${listId}`);
            const $items = $list.find('.usp-item');
            const stopNumber = Number($list.data('usp-stop-number')) || 3;

            const isExpanded = $button.attr('aria-expanded') === 'true';
            const newState = !isExpanded;

            $button.attr('aria-expanded', String(newState));
            $button.toggleClass('active');
            $list.toggleClass('is-expanded', newState);

            if (newState) {
                // Show hidden items with slideDown
                $items.each(function handleEachItem(index) {
                    if (index >= stopNumber) {
                        $(this)
                            .removeClass('usp-item--hidden')
                            .removeAttr('inert')
                            .stop(true, true)
                            .slideDown(300);
                    }
                });
            } else {
                // Hide items again with slideUp
                $items.each(function handleHiddenItems(index) {
                    if (index >= stopNumber) {
                        $(this)
                            .attr('inert', '')
                            .stop(true, true)
                            .slideUp(300, function slideUpCallback() {
                                $(this).addClass('usp-item--hidden');
                            });
                    }
                });
            }

            // Update button text if using localized resources
            const textKey = newState
                ? 'subscription.addons.hidedetails'
                : 'subscription.addons.seedetails';
            const newText = window.resources && window.resources[textKey];
            if (newText) {
                $button.text(newText);
            }
        });
    };

    return {
        init() {
            initSwipers();
            quickLinkSwiper();
            groupLessonsDateThumbsSwiper();
            groupLessonsDayListSwiper();
            initEquipmentSwiper();
            clubsNearbySlider();
            handleSwiperMembership();
            initEventListeners();
            equalizeMembershipPrices();
            equalizeClubsNearby();
            scrollToLinks();
            groupLessonsDayListEnter();
            initMembershipUspToggle();
            initGtmEvents();

            // Read More / Read Less  > Accordion Style
            initReadMoreToggle({
                triggerSelector: '.js-seo-text',
                text1Selector: '.js-seo-text-1',
                text2Selector: '.js-seo-text-2',
                readMoreTextKey: 'store.details.about.read.more',
                readLessTextKey: 'store.details.about.read.less',
            });
            initReadMoreToggle({
                triggerSelector: '.js-find-us-read-more',
                text1Selector: '.js-find-us-text-1',
                text2Selector: '.js-find-us-text-2',
                readMoreTextKey: 'store.details.find.us.read.more',
                readLessTextKey: 'store.details.find.us.read.less',
            });
        },
        resize() {
            equalizeMembershipPrices();
            equalizeClubsNearby();
        },
    };
})();

export default (() => {
    $(document).ready(Details.init);
    $(window).on('resize', debounce(Details.resize, 300));
})();
